import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Circle, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Power, Check, X, Flame, Clock, VolumeX, Volume2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { usePT } from "@/lib/pt";
import { useRingtone, ringOnce, alertInBackground } from "@/lib/ringtone";

export const OnlineToggle = ({ token, online, onChange }) => {
  const { pt } = usePT();
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    if (!online && "Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
    try { const { data } = await api.post(`/partner/${token}/online`, { online: !online }); onChange(data.online); toast.success(data.online ? "You're online. New pickups near you will pop up here." : "You're offline. No new pickup offers."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button onClick={flip} disabled={busy} data-testid="partner-online-toggle"
      className={`flex w-full items-center justify-between gap-3 rounded-3xl p-5 text-left text-white shadow-lg transition-colors active:scale-[.98] ${online ? "bg-[#2E9E6B]" : "bg-[#4A6358]"}`}>
      <span className="min-w-0">
        <span className="block font-display text-2xl font-extrabold" data-testid="partner-online-state">{pt(online ? "You're online" : "You're offline")}</span>
        <span className="text-sm text-white/90">{pt(online ? "Pickups near you come here. Keep the app open." : "Go online to receive pickup requests near you.")}</span>
      </span>
      <span className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full ${online ? "bg-white text-[#2E9E6B] ks-earth-glow" : "bg-white/20"}`}><Power className="h-9 w-9" /></span>
    </button>
  );
};

const left = (iso) => Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));

const Offer = ({ o, token, onDone }) => {
  const { pt } = usePT();
  const [s, setS] = useState(left(o.expires_at));
  const [busy, setBusy] = useState(false);
  useEffect(() => { const id = setInterval(() => setS(left(o.expires_at)), 1000); return () => clearInterval(id); }, [o.expires_at]);
  useEffect(() => { if (s === 0) onDone(); }, [s]); // eslint-disable-line
  const answer = async (accept) => {
    setBusy(true);
    try { await api.post(`/partner/${token}/offers/${o.booking_id}`, { accept }); toast.success(accept ? "Pickup accepted. It's in your list now." : "Offer passed to the next partner."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); onDone(); }
  };
  return (
    <div className="ks-rise space-y-3 rounded-3xl border-4 border-[#E05C3A] bg-white p-5 shadow-xl" data-testid={`offer-${o.booking_id}`}>
      <div className="flex items-center justify-between">
        <p className="font-display text-xl font-extrabold text-[#0D3B2B]">{pt("New pickup request")}</p>
        <span className="flex items-center gap-1 rounded-full bg-[#FDF0EC] px-3 py-1 text-sm font-bold text-[#E05C3A]" data-testid={`offer-timer-${o.booking_id}`}><Clock className="h-4 w-4" />{s}s</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#F4F8F5]"><div className="h-full bg-[#E05C3A] transition-[width] duration-1000" style={{ width: `${(s / 120) * 100}%` }} /></div>
      <p className="text-lg text-[#0D3B2B]"><b>{o.km != null ? `${o.km} ${pt("km away")}` : pt("Near you")}</b>{o.area ? ` · ${o.area}` : ""}</p>
      <p className="text-base text-[#4A6358]">{o.date} · {o.slot_label}<br />{o.scrap_types.join(", ")}{o.est_weight ? ` · ~${o.est_weight} kg` : ""}</p>
      <div className="grid grid-cols-2 gap-2">
        <button disabled={busy} onClick={() => answer(false)} data-testid={`offer-decline-${o.booking_id}`} className="flex h-16 items-center justify-center gap-2 rounded-2xl border-2 border-[#D2E0D8] text-lg font-bold text-[#4A6358]"><X className="h-7 w-7" />{pt("Decline")}</button>
        <button disabled={busy} onClick={() => answer(true)} data-testid={`offer-accept-${o.booking_id}`} className="flex h-16 items-center justify-center gap-2 rounded-2xl bg-[#2E9E6B] text-lg font-bold text-white"><Check className="h-7 w-7" />{pt("Accept")}</button>
      </div>
    </div>
  );
};

export const useOffers = (token) => {
  const [offers, setOffers] = useState([]);
  const load = useCallback(() => api.get(`/partner/${token}/offers`).then((r) => {
    setOffers((prev) => { if (r.data.some((o) => !prev.find((p) => p.booking_id === o.booking_id))) alertInBackground("New pickup request", "Tap to accept before it goes to another partner."); return r.data; });
  }).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  return [offers, load];
};

export const Offers = ({ offers, token, reload }) => {
  const [muted, setMuted] = useState(false);
  useRingtone(offers.length > 0 && !muted);
  useEffect(() => { if (!offers.length) setMuted(false); }, [offers.length]);
  if (!offers.length) return null;
  return (
  <div className="fixed inset-0 z-[1200] flex items-end justify-center bg-[#0D3B2B]/60 backdrop-blur-sm sm:items-center" data-testid="offer-overlay">
    <div className="ks-sheet w-full max-w-md space-y-3 p-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
      <button onClick={() => setMuted(!muted)} data-testid="offer-mute-btn" className="ml-auto flex items-center gap-1 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-[#0D3B2B]">
        {muted ? <><Volume2 className="h-4 w-4" />Ring again</> : <><VolumeX className="h-4 w-4" />Silence ringtone</>}</button>
      <Offer key={offers[0].booking_id} o={offers[0]} token={token} onDone={reload} />
      {offers.length > 1 && <p className="text-center text-xs font-bold text-white">+{offers.length - 1} more waiting</p>}
    </div>
  </div>
  );
};

export const RingTest = () => (
  <button onClick={ringOnce} data-testid="partner-ring-test-btn" className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#D2E0D8] bg-white text-sm font-bold text-[#0D3B2B]">
    <Volume2 className="h-5 w-5" />Test pickup ringtone</button>
);

export const PartnerMap = ({ me, nearby, jobs, tall }) => {
  if (!me) return null;
  return (
    <div className="space-y-2" data-testid="partner-map">
      <div className={`relative isolate z-0 overflow-hidden rounded-3xl border border-[#D2E0D8] ${tall ? "h-[60vh]" : "h-56"}`}>
        <MapContainer center={[me.lat, me.lng]} zoom={13} className="h-full w-full" scrollWheelZoom={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          <Circle center={[me.lat, me.lng]} radius={5000} pathOptions={{ color: "#2E9E6B", weight: 1, fillOpacity: 0.05 }} />
          <CircleMarker center={[me.lat, me.lng]} radius={10} pathOptions={{ color: "#fff", weight: 3, fillColor: "#2E9E6B", fillOpacity: 1 }}><Tooltip>You</Tooltip></CircleMarker>
          {nearby.map((n) => {
            const c = n.count || 1;
            const fill = c >= 4 ? "#9A3412" : c >= 2 ? "#EA580C" : "#FDBA74";
            return [900, 600, 350].map((r, k) => (
              <Circle key={`${n.lat},${n.lng},${r}`} center={[n.lat, n.lng]} radius={r} pathOptions={{ stroke: false, fillColor: fill, fillOpacity: 0.12 + k * 0.1 }}>
                {k === 2 && <Tooltip>{c} open {c === 1 ? "request" : "requests"} in this area · ~{n.km} km</Tooltip>}
              </Circle>
            ));
          })}
          {jobs.filter((j) => j.lat != null).map((j) => <CircleMarker key={j.booking_id} center={[j.lat, j.lng]} radius={8} pathOptions={{ color: "#fff", weight: 2, fillColor: "#0D3B2B", fillOpacity: 1 }}><Tooltip>Stop {j.seq}</Tooltip></CircleMarker>)}
        </MapContainer>
      </div>
      <p className="flex flex-wrap gap-3 text-[11px] font-semibold text-[#4A6358]">
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-[#2E9E6B]" />You</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-[#EA580C]" />Open requests, area only ({nearby.reduce((a, n) => a + (n.count || 1), 0)})</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-[#0D3B2B]" />My stops</span>
      </p>
    </div>
  );
};

export const BusyAreas = ({ areas: all }) => {
  const areas = (all || []).filter((a) => a.expected >= 0.5);
  if (!areas.length) return null;
  return (
  <div className="rounded-3xl bg-[#FDF0EC] p-4" data-testid="partner-busy-areas">
    <p className="flex items-center gap-1 text-sm font-bold text-[#E05C3A]"><Flame className="h-4 w-4" />Busy areas today</p>
    <p className="mt-1 text-xs text-[#4A6358]">{areas.map((a) => `${a.area} (~${Math.round(a.expected)} pickups)`).join(" · ")}. Stay close to these for more pickups.</p>
  </div>
  );
};
